import { Link } from "react-router-dom";
import { ArrowLeft, Wrench, Sparkles, Users, TrendingUp, Briefcase, Monitor } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useState, useRef, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import homesBackground from "@/assets/homes-background.jpg";
import PropertyMap from "@/components/PropertyMap";
import ScrollToTop from "@/components/ScrollToTop";
import PropertyDetailsPopup from "@/components/PropertyDetailsPopup";
import ContactForm from "@/components/ContactForm";
import { Button } from "@/components/ui/button";
import SkylineHero from "@/components/SkylineHero";

interface Location {
  name: string;
  address: string;
  units: number;
  description?: string;
  amenities?: string[];
}

interface PropertyDetails {
  name: string;
  description: string;
  address: string;
  units: number;
  yearBuilt: number;
  rating: number;
  images: string[];
  amenities: string[];
  locations?: Location[];
}

const Homes = () => {
  const { t } = useLanguage();
  const [selectedProperty, setSelectedProperty] = useState<PropertyDetails | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const partners = [
    { name: "Partner 1", logo: "/lovable-uploads/625a540f-6aab-4d63-bbd9-eb264a215835.png" },
    { name: "Partner 2", logo: "/lovable-uploads/60cd49dd-2aca-4ee0-be5d-334aa1ba9502.png" },
    { name: "Partner 3", logo: "/lovable-uploads/46e79b45-8b05-46f1-9f7c-042a95338507.png" },
    { name: "Partner 4", logo: "/lovable-uploads/7697c8c9-2f33-4972-b78e-b741794d742c.png" },
    { name: "Partner 5", logo: "/lovable-uploads/c28cb9d8-5d85-4f09-a827-14006f8fc66e.png" },
    { name: "Partner 6", logo: "/lovable-uploads/c3b90e33-9512-4480-8e92-2acece9da9aa.png" },
    { name: "Marakez", logo: "/lovable-uploads/6060c81f-e01a-481b-ab15-f4b3a36213b2.png" },
    { name: "Partner 8", logo: "/lovable-uploads/8235243c-77c6-4018-8fac-ae59f1175386.png" },
    { name: "Al Ahly Pharos", logo: "/lovable-uploads/1d1004c6-6b8f-4382-967a-289f0a897c18.png" },
    { name: "Partner 10", logo: "/lovable-uploads/f4dd9d91-87b0-41a2-a040-278fbe76024a.png" },
  ];

  // Shuffle function to randomize partners order
  const shuffleArray = (array: typeof partners) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Create seamless infinite loop by tripling the array
  const shuffledPartners = shuffleArray(partners);
  const infinitePartners = [...shuffledPartners, ...shuffledPartners, ...shuffledPartners];

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    // Start from the beginning (left)
    const itemWidth = 408; // 400px width + 8px gap  
    const totalItems = shuffledPartners.length;
    carousel.scrollLeft = 0;

    let animationId: number;
    const scrollSpeed = 1;

    const scroll = () => {
      carousel.scrollLeft += scrollSpeed;
      
      // When we've scrolled past the first set, reset to beginning
      const maxScroll = totalItems * itemWidth;
      if (carousel.scrollLeft >= maxScroll) {
        carousel.scrollLeft = 0;
      }
      
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [shuffledPartners.length]);

  const propertyDetails = {
    "Hannaux Aparthotel": {
      name: "Hannaux Aparthotel",
      description: "Hannaux Apart-Hotel offers 42 stylish studio units in Downtown Cairo, combining modern design with comfort. Each studio includes a fully equipped kitchen and premium amenities, catering to both short-term and extended stays. Its central location provides easy access to cultural landmarks, shopping, and dining, making it an ideal choice for business travelers and tourists seeking luxury and convenience.",
      address: "Downtown Cairo, Egypt",
      units: 42,
      yearBuilt: 2022,
      rating: 4.8,
      images: [
        "/lovable-uploads/hannaux-living-room.jpg", // Living area with sofa and artwork
        "/lovable-uploads/hannaux-exterior-street.jpg", // Exterior street view with statue
        "/lovable-uploads/hannaux-bedroom-window.jpg", // Bedroom with window view
        "/lovable-uploads/hannaux-bedroom-art.jpg", // Bedroom with wall art
        "/lovable-uploads/hannaux-bedroom-modern.jpg", // Modern bedroom with blue pillows
        "/lovable-uploads/hannaux-kitchen.jpg", // Kitchen area with plants
        "/lovable-uploads/hannaux-building-view.jpg", // Building exterior view
        "/lovable-uploads/hannaux-exterior-classic.jpg" // Classic exterior architecture
      ],
      amenities: ["Welcoming lobby and reception lounge", "Co-working space for residents and entrepreneurs", "Café and concept retail space", "In-house art gallery & rotating exhibitions", "24/7 concierge and guest services", "Fully equipped studios with modern in-room amenities"]
    },
    "Grey Studios": {
      name: "Grey Studios",
      description: "Urban sophistication with tailored amenities. Grey Studios represents the pinnacle of modern apartment living, featuring sleek contemporary design and premium finishes throughout. Each unit is carefully crafted to maximize space and functionality while maintaining an aesthetic of refined minimalism.",
      address: "Multiple Locations: Ouzonoian (Downtown Cairo), Nabi Daniel (Alexandria), Degla (Alexandria)",
      units: 87,
      yearBuilt: 2023,
      rating: 4.7,
      images: [
        "/lovable-uploads/grey-studios-bedroom-modern.jpg", // Modern bedroom with black pendant lights and artwork
        "/lovable-uploads/grey-studios-staircase.jpg", // Elegant staircase with marble floors and Art Deco railings
        "/lovable-uploads/grey-studios-hallway.jpg", // Hallway with yellow ceiling accents and blue doors
        "/lovable-uploads/grey-studios-bedroom-parquet.jpg", // Bedroom area with parquet flooring and gray armchair
        "/lovable-uploads/grey-studios-bedroom-artwork.jpg" // Bedroom with white bedding and framed artwork above
      ],
      amenities: ["Air Conditioning", "Kitchenette with Appliances", "Washing Machine", "Private Bathroom", "Balcony", "Free WiFi", "TV with Satellite Channels", "Electric Kettle", "Refrigerator", "Private Entrance", "Free Toiletries", "Hairdryer"],
      locations: [
        {
          name: "Grey Studios - Ouzonoian",
          address: "Historic Ouzonian Building, Downtown Cairo, Egypt",
          units: 40,
          description: "Urban living meets cultural heritage at Grey Studios Ouzonian. Designed with the same commitment to contemporary elegance and refined minimalism, these studios bring modern functionality into the heart of Cairo's most iconic district. Each unit combines sleek interiors with thoughtful layouts, offering residents a sophisticated retreat within the city's vibrant energy. Positioned in the historic Ouzonian building in Downtown Cairo, Grey Studios blends architectural character with modern comfort — placing residents steps away from art, culture, and the pulse of Cairo's urban revival."
        },
        {
          name: "Grey Studios - Nabi Daniel", 
          address: "Nabi Daniel, Alexandria, Egypt",
          units: 22,
          description: "Urban sophistication with tailored amenities. Grey Studios represents the pinnacle of modern apartment living, featuring sleek contemporary design and premium finishes throughout.",
          amenities: ["Sea Views"],
          images: [
            "/lovable-uploads/6b1278f6-9e34-492d-833b-d6b6bd9159fd.png", // Modern living area with contemporary design
            "/lovable-uploads/6cb7668c-a434-45dd-a443-a000b4c3c3a1.png", // Stylish bedroom with modern amenities
            "/lovable-uploads/668aef3d-d78b-4b0f-8e7e-c813886d857a.png", // Contemporary bathroom with premium fixtures
            "/lovable-uploads/678bf767-6712-45c9-bda5-33d08d7e1a09.png", // Kitchen with modern appliances
            "/lovable-uploads/6d37aa27-9811-4a76-9592-3e412b5e970c.png"  // Studio layout with integrated design
          ]
        },
        {
          name: "Grey Studios - Degla",
          address: "Corniche Alexandria Little Venice, Alexandria, Egypt", 
          units: 25,
          description: "Comfortable Living: Grey Studio Apartments Corniche Alexandria Little Venice in Alexandria offers a terrace and free WiFi. Each apartment includes air-conditioning, a kitchenette, washing machine, private bathroom, and stunning Corniche views. Located 30 mi from Borg el Arab International Airport, near Shatibi Beach, Alexandrina Library, and Alexandria Stadium.",
          amenities: ["Sea Views"],
          images: [
            "/lovable-uploads/7f255d89-b4cd-4408-a41d-269724e77c36.png", // Elegant bedroom with sea view balcony
            "/lovable-uploads/75f3197e-2515-4fbf-bcf7-d6cd5670dc03.png", // Modern loft-style studio with mezzanine
            "/lovable-uploads/dab97f6a-639c-4589-b970-d5dd2fa798b7.png", // Contemporary bathroom with shower
            "/lovable-uploads/f2a37aaf-0c75-4c43-b906-a0617db63c00.png", // Stylish bathroom with modern fixtures
            "/lovable-uploads/207f5a48-a950-4566-bd3f-e042a6b065b9.png", // Conference room with sea views
            "/lovable-uploads/db410315-e3a7-41d2-96cf-7ce1cd834657.png", // Historic building facade
            "/lovable-uploads/b91c2ff7-e756-4d8b-80b4-fd55aee78d0c.png", // Modern kitchen with island and pendant lighting
            "/lovable-uploads/b5995aa8-c58f-4be1-940b-e634ea409ebd.png"  // Contemporary kitchen with bar seating
          ]
        }
      ]
    },
    "Giza - Swiss Tower": {
      name: "Giza - Swiss Tower",
      description: "Giza Swiss Tower Apartments offer modern residences with panoramic Nile views, blending functional design with everyday comfort. Each apartment is crafted to provide a contemporary living experience, with clean finishes, efficient layouts, and a prime location that connects residents to the cultural and commercial heart of Giza. The property combines urban convenience with the timeless appeal of riverside living.",
      address: "Giza, Egypt",
      units: 51,
      yearBuilt: 2021,
      rating: 4.9,
      images: [
        "/lovable-uploads/580acdf6-c655-495a-854d-0850464beefa.png", // Studio apartment with living area and bedroom
        "/lovable-uploads/b1b6874e-8254-4110-839d-bf58af18682a.png", // Modern bedroom with city views
        "/lovable-uploads/29981dd6-e53b-4cc6-9ae3-6812675670c2.png", // Elegant bedroom with panoramic windows
        "/lovable-uploads/e9a4442a-f8c1-4ff1-bda5-f5691c80507a.png", // Fully equipped modern kitchen
        "/lovable-uploads/5598649e-0f96-47d3-8d8a-10900c9d252f.png", // Living room with Nile views
        "/lovable-uploads/43f58667-87c9-46ac-a0f6-4f76407ae275.png", // Studio with integrated kitchen and sleeping area
        "/lovable-uploads/851fdb28-84ca-4116-8dc6-f574bed11d26.png", // Spacious living area with river views
        "/lovable-uploads/ac120a0f-ee3b-4173-820c-147325f30973.png"  // Original image
      ],
      amenities: ["Panoramic Nile views", "Modern, fully serviced apartments", "24/7 security and reception", "High-speed Wi-Fi and smart connectivity", "Housekeeping and maintenance services", "Parking facilities"]
    },
    "Daoud Villas": {
      name: "Daoud Villas",
      description: "Daoud Villas reimagines Zamalek's historic charm through a boutique aparthotel concept that transforms three classic villas into a contemporary serviced-living destination. Tucked away in one of Cairo's most sought-after districts, Daoud Villas offers a rare blend of privacy, elegance, and community. The property features lush garden spaces and thoughtfully designed studios and apartments, delivering the comfort of home with the services of a boutique hotel. Residents and guests enjoy a refined yet relaxed atmosphere, where heritage architecture meets modern hospitality.",
      address: "Zamalek, Cairo, Egypt",
      units: 32,
      yearBuilt: 2022,
      rating: 4.6,
      images: [
        "/lovable-uploads/bee5058a-e736-4345-acfa-db640a9423e4.png", // Modern studio with wood kitchen, dining area, and bedroom
        "/lovable-uploads/b5f77953-a0f0-49ca-9cfa-06db7498022d.png", // Kitchen area with wood cabinets and red door
        "/lovable-uploads/44c813fb-1ec3-4f77-a34a-b014794a6d06.png", // Bedroom with art gallery wall and kitchenette
        "/lovable-uploads/2dcc95d8-6a61-46bd-b521-9596c9ac29dc.png", // Kitchen with wood cabinets and red door details
        "/lovable-uploads/44a04c03-aae9-4cf9-a135-eb71a854a798.png", // Bedroom with gallery wall and TV
        "/lovable-uploads/7623c7b7-47ae-4db4-873d-10ffffaceef1.png", // Balcony/terrace with city views and furniture
        "/lovable-uploads/8d237724-3105-45e1-bbbc-8c277fc00475.png"  // Bedroom with dual windows and workspace
      ],
      amenities: ["Private garden and outdoor lounge", "Lobby & reception services", "Housekeeping and laundry services", "Fully equipped kitchens in all units", "High-speed Wi-Fi and smart in-room technology", "24/7 concierge support"]
    },
    "Brassbell Kasr El Nile": {
      name: "Brassbell Kasr El Nile",
      description: "Brassbell Kasr El Nile is an exclusive serviced living residence set within a beautifully restored neoclassical building just steps from Tahrir Square. With only 12 thoughtfully designed apartments, Kasr El Nile offers an intimate, boutique experience that blends heritage charm with contemporary comfort. Its interiors pay homage to classical architecture while incorporating modern design elements and lifestyle conveniences, making it a rare sanctuary at the heart of Cairo's most vibrant district.",
      address: "Downtown Cairo, Egypt",
      units: 12,
      yearBuilt: 2025,
      rating: 4.8,
      images: [
        "/lovable-uploads/kasr-el-nile-unit1.jpg", // Modern bedroom with artwork and lighting (cover)
        "/lovable-uploads/kasr-el-nile-exterior.jpg", // Building exterior with illuminated entrance
        "/lovable-uploads/kasr-el-nile-staircase.jpg", // Interior staircase with modern railings
        "/lovable-uploads/kasr-el-nile-entrance.jpg", // Apartment entrance with arched design
        "/lovable-uploads/kasr-el-nile-view.jpg", // City street view from apartment window
        "/lovable-uploads/kasr-el-nile-unit3.jpg", // Bedroom with decorative pillows
        "/lovable-uploads/kasr-el-nile-unit4-living.jpg", // Living room with curved windows
        "/lovable-uploads/kasr-el-nile-unit4-bedroom.jpg" // Bedroom and living area with arched divider
      ],
      amenities: ["Fully furnished serviced apartments with neoclassical interiors", "Residents' lounge and reception area", "Housekeeping and on-demand maintenance", "Concierge services", "High-speed Wi-Fi and smart apartment features", "Secure access and 24/7 support"]
    },
    "Brassbell Zamalek Suites": {
      name: "Brassbell – Zamalek Suites",
      description: "Brassbell – Zamalek Suites offers a collection of 30 stylish, fully serviced apartments elegantly positioned in Cairo's diplomatic district. Just steps from the Marriott and set within an iconic building on Hassan Sabry Street, every suite combines modern comfort with cultural gravitas. Sleek interiors, thoughtful layout, and elevated design come together to create a living experience that is both cosmopolitan and distinctly local.",
      address: "Hassan Sabry Street, Zamalek, Cairo, Egypt",
      units: 30,
      yearBuilt: 2024,
      rating: 4.7,
      images: [
        "/lovable-uploads/fd03ff4f-3a0d-4e96-96f6-ad5bf49beabf.png", // Modern living room with dining area and warm lighting
        "/lovable-uploads/dd0c5855-84ee-4f4f-9756-fabe1aba4917.png", // Open concept kitchen and dining space
        "/lovable-uploads/a7b514b7-07ce-456b-b217-0b5ae42b366e.png", // Studio apartment with glass partition
        "/lovable-uploads/77f52246-7359-483b-8230-028eea5e8398.png", // Stylish bedroom with modern amenities
        "/lovable-uploads/07cc0794-5055-4fac-9c3a-b6903204cb0b.png", // Contemporary studio with kitchen integration
        "/lovable-uploads/d1a34951-76e6-432b-9862-9bc4ef79e3c2.png", // Modern studio with glass divider and kitchen
        "/lovable-uploads/556259bf-939e-4aa0-b843-d8b48ccfcd32.png", // Comfortable living room with modern furnishing
        "/lovable-uploads/33cf918f-6351-423a-b122-9d79f185cea0.png"  // Building exterior in Zamalek district
      ],
      amenities: ["Fully furnished serviced apartments", "24-hour front desk & concierge services", "High-speed Wi-Fi and smart in-room features", "In-suite laundry facilities", "Modern kitchenettes and dining zones", "Daily housekeeping & maintenance", "Secure elevator access & 24-hour security", "Flat-screen TV and climate control"]
    }
  };

  const handlePropertyClick = (propertyName: string) => {
    setSelectedProperty(propertyDetails[propertyName as keyof typeof propertyDetails]);
    setIsPopupOpen(true);
  };
  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero Section */}
      <section 
        className="min-h-[70vh] md:min-h-screen flex items-center justify-center relative">
        {/* White overlay */}
        <div className="absolute inset-0 bg-white/80" />

        {/* Skyline drawing animation — matches hotels hero */}
        <SkylineHero className="opacity-25" />
        
        {/* Back button */}
        <Link 
          to="/"
          className="absolute top-4 left-4 md:top-8 md:left-8 flex items-center text-black hover:text-gray-700 transition-colors z-10 text-sm md:text-base">
          <ArrowLeft className="mr-1 md:mr-2" size={16} />
          <span className="hidden sm:inline">{t("common.backToHome")}</span>
          <span className="sm:hidden">Back</span>
        </Link>

        {/* Content */}
        <div className="relative z-10 text-center text-black px-4 md:px-6">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light mb-4 md:mb-8 tracking-tight leading-tight sm:leading-[0.9] text-foreground">
            Brassbell Homes
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-light max-w-3xl mx-auto leading-relaxed">
            {t("homes.description")}
          </p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-8 md:py-16 lg:py-20 px-4 md:px-6 bg-background">
        <div className="max-w-4xl mx-auto text-center">
          <div 
            className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in"
            style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
          >
            <p className="text-sm md:text-base lg:text-lg text-black leading-relaxed mb-6 md:mb-12">
              Brassbell Homes stands as the leading operator of serviced apartments in the region, managing a portfolio of over 1,250 units strategically located in central, high-demand areas. Our reputation as the most trusted operator is built on delivering a fully fledged end-to-end solution for developers and landlords, guiding them at every step of transforming underutilized real estate into revenue-generating properties.
            </p>
            <p className="text-sm md:text-base lg:text-lg text-black leading-relaxed">
              Our involvement begins early through consultancy services including feasibility studies and market analysis to assess potential and optimize investment decisions. From there, we lead the design phase, crafting tailored floorplans that blend functionality with lifestyle appeal.
            </p>
          </div>
        </div>
      </section>

      {/* End-to-End Process Section */}
      <section className="py-8 md:py-16 lg:py-20 px-4 md:px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div 
            className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in text-center mb-16"
            style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-foreground mb-4 md:mb-8">
              Our End-to-End Process
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              Transforming Real Estate into Thriving Residences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
            {[
              {
                number: "1",
                title: "Consultancy & Feasibility Studies",
                description: "We begin by conducting thorough market research and financial analysis to assess the potential of a property or development opportunity. This phase includes evaluating the location's demand dynamics, competitive landscape, and projected returns."
              },
              {
                number: "2", 
                title: "Design & Floorplan Development",
                description: "Based on feasibility insights, we collaborate closely with developers to create optimized floorplans and designs that balance space efficiency, resident comfort, and the unique character of each location."
              },
              {
                number: "3",
                title: "Fit-out & FF&E Procurement", 
                description: "Leveraging a trusted network of suppliers and industry partners, Brassbell Homes manages the procurement of interior finishes, furniture, fixtures, and equipment (FF&E). We collaborate closely with Kanjo Design House on most projects."
              },
              {
                number: "4",
                title: "Seamless Transition to Operations",
                description: "Once the physical development is complete, we provide a smooth handover to our centralized operations team. Utilizing advanced systems and technology, we manage all aspects of property operation."
              }
            ].map((step, index) => (
              <div 
                key={step.number}
                className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in p-4 md:p-6 lg:p-8 bg-background rounded-xl hover:shadow-lg hover:-translate-y-2 transition-all duration-300"
                style={{ animationDelay: `${0.6 + index * 0.2}s`, animationFillMode: 'forwards' }}
              >
                <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-4 md:space-x-6">
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-lg md:text-xl font-light text-primary">{step.number}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl lg:text-2xl font-light text-foreground mb-2 md:mb-4">{step.title}</h3>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Partnerships Section */}
      <section className="py-4 md:py-8 lg:py-10 px-4 md:px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <div 
            className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in text-center mb-4"
            style={{ animationDelay: '1.4s', animationFillMode: 'forwards' }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-white mb-2 md:mb-3">
              Trusted Partnerships That Drive Value
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-white/70 max-w-4xl mx-auto leading-relaxed mb-3 md:mb-4">
              Brassbell Homes boasts strong, enduring partnerships with some of the region's most respected developers and real estate stakeholders.
            </p>
          </div>

          <div className="w-full overflow-hidden bg-black">
            <div 
              ref={carouselRef}
              className="flex gap-0 overflow-hidden animate-scroll-left bg-black"
            >
              {infinitePartners.map((partner, index) => (
                <div
                  key={`${partner.name}-${index}`}
                  className="flex-shrink-0 flex items-center justify-center p-1 sm:p-2 min-w-[300px] sm:min-w-[400px] h-[120px] sm:h-[180px] bg-black"
                >
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                    style={{ maxWidth: '360px', maxHeight: '140px' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Centralized Operations Section */}
      <section className="py-8 md:py-16 lg:py-20 px-4 md:px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div 
            className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in text-center mb-16"
            style={{ animationDelay: '1.8s', animationFillMode: 'forwards' }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-foreground mb-4 md:mb-8">
              Centralized Operations Infrastructure
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              At the heart of Brassbell Homes' success is our centralized operations infrastructure, a sophisticated system designed to ensure seamless coordination across all service functions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {[
              {
                title: "Maintenance",
                description: "Our proactive maintenance team monitors and addresses property needs promptly, employing predictive tools and scheduled inspections.",
                icon: Wrench
              },
              {
                title: "Housekeeping & Laundry", 
                description: "With rigorous quality controls, our housekeeping and laundry services maintain impeccable standards of cleanliness and hygiene.",
                icon: Sparkles
              },
              {
                title: "Guest Relations",
                description: "Our dedicated guest relations professionals are accessible 24/7, providing personalized support and swift issue resolution.",
                icon: Users
              },
              {
                title: "Revenue Management",
                description: "Utilizing advanced data analytics and market intelligence, our revenue management team dynamically adjusts pricing strategies.",
                icon: TrendingUp
              },
              {
                title: "Corporate Sales",
                description: "A specialized sales team targets corporate clients, cultivating long-term contracts through tailored packages.",
                icon: Briefcase
              },
              {
                title: "Technology Integration",
                description: "Proprietary platforms enable real-time communication, centralized data oversight, and transparent reporting.",
                icon: Monitor
              }
            ].map((service, index) => (
              <div 
                key={service.title}
                className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in p-4 md:p-6 bg-background rounded-xl hover:shadow-lg hover:-translate-y-2 transition-all duration-300"
                style={{ animationDelay: `${2.0 + index * 0.1}s`, animationFillMode: 'forwards' }}
              >
                <div className="flex items-center mb-3 md:mb-4">
                  <service.icon className="h-6 w-6 md:h-8 md:w-8 text-primary mr-2 md:mr-3" />
                  <h3 className="text-lg md:text-xl font-light text-foreground">{service.title}</h3>
                </div>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>

          <div 
            className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in text-center mt-8 md:mt-12 p-4 md:p-6 bg-primary/5 rounded-xl"
            style={{ animationDelay: '2.6s', animationFillMode: 'forwards' }}
          >
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> PMS, Channel Manager, Tech-stack built in-house
            </p>
          </div>
        </div>
      </section>

      {/* Featured Aparthotels Section */}
      <section className="py-8 md:py-16 lg:py-20 px-4 md:px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div 
            className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in text-center mb-16"
            style={{ animationDelay: '3.0s', animationFillMode: 'forwards' }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-foreground mb-4 md:mb-8">
              Featured Aparthotels
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {[
              {
                name: "Hannaux Aparthotel",
                description: "Hannaux Apart-Hotel embodies the nostalgic, retro spirit of Downtown Cairo's heritage, thoughtfully reinterpreted through a modern design lens. It captures the cultural soul of the area, creating a living environment that honors the past while embracing contemporary urban life."
              },
              {
                name: "Brassbell Kasr El Nile",
                description: "Exclusive neoclassical serviced living near Tahrir Square."
              },
              {
                name: "Brassbell Zamalek Suites",
                description: "Stylish serviced apartments in Cairo's diplomatic district."
              },
              {
                name: "Grey Studios", 
                description: "Urban sophistication with tailored amenities."
              },
              {
                name: "Giza - Swiss Tower",
                description: "Iconic residences in a landmark location."
              },
              {
                name: "Daoud Villas",
                description: "Boutique aparthotel concept transforming Zamalek's historic charm."
              }
            ].map((hotel, index) => (
              <button 
                key={hotel.name}
                onClick={() => handlePropertyClick(hotel.name)}
                className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in p-4 md:p-6 lg:p-8 bg-background rounded-xl hover:shadow-lg hover:-translate-y-2 transition-all duration-300 text-left w-full"
                style={{ animationDelay: `${3.2 + index * 0.2}s`, animationFillMode: 'forwards' }}
              >
                {hotel.name === "Hannaux Aparthotel" && (
                  <div className="aspect-video mb-3 md:mb-4 overflow-hidden rounded-lg">
                    <img 
                      src="/lovable-uploads/2414e5ec-23e9-4adc-b88d-65d2fb3a3869.png" 
                      alt="Hannaux Apart-Hotel - Modern industrial design with blue kitchen and warm lighting"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {hotel.name === "Grey Studios" && (
                  <div className="aspect-video mb-4 overflow-hidden rounded-lg">
                    <img 
                      src="/lovable-uploads/grey-studios-bedroom-modern.jpg" 
                      alt="Grey Studios - Modern bedroom with black pendant lights and artwork"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {hotel.name === "Giza - Swiss Tower" && (
                  <div className="aspect-video mb-4 overflow-hidden rounded-lg">
                    <img 
                      src="/lovable-uploads/ac120a0f-ee3b-4173-820c-147325f30973.png" 
                      alt="Swiss Tower - Elegant living room with panoramic city views"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {hotel.name === "Daoud Villas" && (
                  <div className="aspect-video mb-4 overflow-hidden rounded-lg">
                    <img 
                      src="/lovable-uploads/bee5058a-e736-4345-acfa-db640a9423e4.png" 
                      alt="Daoud Villas - Modern studio with wood kitchen, dining area, and bedroom"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {hotel.name === "Brassbell Kasr El Nile" && (
                  <div className="aspect-video mb-4 overflow-hidden rounded-lg">
                    <img 
                      src="/lovable-uploads/kasr-el-nile-unit1.jpg" 
                      alt="Brassbell Kasr El Nile - Modern bedroom with artwork and lighting"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {hotel.name === "Brassbell Zamalek Suites" && (
                  <div className="aspect-video mb-4 overflow-hidden rounded-lg">
                    <img 
                      src="/lovable-uploads/fd03ff4f-3a0d-4e96-96f6-ad5bf49beabf.png" 
                      alt="Brassbell Zamalek Suites - Modern living room with dining area and warm lighting"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <h3 className="text-lg md:text-xl lg:text-2xl font-light text-foreground mb-2 md:mb-4">{hotel.name}</h3>
                <div className="mt-3 md:mt-4 text-primary text-xs md:text-sm font-medium">
                  Click to view details →
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership CTA Section */}
      <section className="py-8 md:py-16 lg:py-20 px-4 md:px-6 bg-background">
        <div className="max-w-4xl mx-auto text-center">
          <div 
            className="opacity-0 translate-y-8 transition-all duration-1000 ease-out animate-fade-in"
            style={{ animationDelay: '4.0s', animationFillMode: 'forwards' }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-foreground mb-4 md:mb-8">
              Partner With Brassbell Homes
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground leading-relaxed mb-4 md:mb-8">
              Brassbell Homes offers more than management—we provide a full ecosystem built on trust, technology, and operational excellence. We invite developers, investors, and collaborators to engage with us and realize the potential of serviced living through a partnership that delivers enduring value and innovation.
            </p>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground leading-relaxed mb-6 md:mb-8">
              Connect with Brassbell Hospitality Group to explore collaborative opportunities that drive growth and redefine residential hospitality.
            </p>
            <Button 
              onClick={() => setIsContactFormOpen(true)}
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <PropertyMap />
      
      {/* Property Details Popup */}
      <PropertyDetailsPopup
        property={selectedProperty}
        open={isPopupOpen}
        onOpenChange={setIsPopupOpen}
        onContactClick={() => setIsContactFormOpen(true)}
      />
      
      {/* Contact Form */}
      <ContactForm
        open={isContactFormOpen}
        onOpenChange={setIsContactFormOpen}
      />
      
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Homes;